import React, { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { FormControl, FormControlProps } from '../FormControl';
import { useAppFormSchema } from '../appFormSchema';
import { AppFormControlContext } from './appFormControlContext';
import { appFormDataTestFactory } from './appFormDataTestFactory';
import { styled } from '../../../styles';
import { Leaves } from '../types';

interface WithName<DATA> extends Omit<FormControlProps, 'error'> {
  name: (Leaves<DATA> & string) | string;
  marginBottom?: React.CSSProperties['marginBottom'];
}

interface WithoutName extends Omit<FormControlProps, 'error'> {
  error?: string;
  submitted?: boolean;
  marginBottom?: React.CSSProperties['marginBottom'];
}

export type AppFormControlProps<DATA> = WithName<DATA> | WithoutName;

export const AppFormControl = <DATA extends {}>({
  children,
  dataTest,
  fullWidth = true,
  marginBottom,
  ...rest
}: AppFormControlProps<DATA>) => {
  const context = useFormikContext<DATA>();
  const schema = useAppFormSchema();
  const submitted = (rest as WithoutName).submitted ?? context.submitCount > 0;
  const disabled = rest.disabled;
  const error = (rest as WithoutName).error ?? ('name' in rest ? (context.errors[rest.name] as string) : undefined);
  const name = 'name' in rest ? rest.name : undefined;
  const required =
    rest.required ?? (name ? (schema?.fields as Record<string, any>)?.[name]?._exclusive?.required : undefined);
  const props = useMemo(
    () => ({
      name,
      disabled,
      dataTest: dataTest ?? name,
      error: submitted ? error : undefined,
      submitted,
      required
    }),
    [name, dataTest, submitted, error, required, disabled]
  );

  return (
    <AppFormControlContext.Provider value={props}>
      <StyledControl
        data-test-app-form-item
        dataTest={appFormDataTestFactory(props.dataTest).control}
        fullWidth={fullWidth}
        {...rest}
        error={!!props.error}
        required={props.required}
        $marginBottom={marginBottom}
      >
        {children}
      </StyledControl>
    </AppFormControlContext.Provider>
  );
};

const StyledControl = styled(FormControl)<{ $marginBottom?: React.CSSProperties['marginBottom'] }>`
  && {
    margin-bottom: ${({ $marginBottom, theme }) => ($marginBottom !== undefined ? $marginBottom : theme.spacingSize.l)};
    label.MuiFormLabel-root + label.MuiFormLabel-root {
      font-weight: normal;
    }
  }
`;
